import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import BuildIcon from '@mui/icons-material/Build';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';

function MonthlyReconditioning() {
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconditioning, setReconditioning] = useState([]);
  const { month, year } = location.state || {};
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReconditioning = async () => {
      try {
        const username = localStorage.getItem('username');
        
        // Convert month name to number (0-11)
        const monthNames = ["January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"];
        const monthNumber = monthNames.indexOf(month);
        
        if (monthNumber === -1) {
          throw new Error('Invalid month name');
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/reports/monthly?username=${username}&month=${monthNumber}&year=${year}`
        );
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch reconditioning data');
        }

        const data = await response.json();
        setReconditioning(data.reconditioning);
      } catch (err) {
        console.error('Reconditioning Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReconditioning();
  }, [month, year]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" mt={5}>
        {error}
      </Typography>
    );
  }

  const totalCost = reconditioning.reduce((sum, record) => sum + parseFloat(record.cost || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/home')}
          sx={{ mb: 2, fontSize: '0.7rem' }}
        >
          Back to Home
        </Button>
        
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={4}>
            <BuildIcon sx={{ fontSize: 40, color: '#4CAF50', opacity: 0.7 }} />
            <Box>
              <Typography variant="h4" gutterBottom>
                Monthly Reconditioning
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {month} {year}
              </Typography>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>VIN</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reconditioning.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{new Date(record.date_occurred).toLocaleDateString()}</TableCell>
                    <TableCell>{`${record.year} ${record.make} ${record.model}`}</TableCell>
                    <TableCell>{record.vin}</TableCell>
                    <TableCell>{record.notes}</TableCell>
                    <TableCell align="right">${parseFloat(record.cost).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalCost.toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </motion.div>
  );
}

export default MonthlyReconditioning;
